import { useCallback, useEffect, useState } from "react";

/** The prompt appears once per user per day, at a random moment in this
 * window after the canvas opens — late enough that there is something to
 * say, random so a whole class is not interrupted at the same second. */
export const SURVEY_MIN_DELAY_MILLISECONDS = 5 * 60_000;
export const SURVEY_MAX_DELAY_MILLISECONDS = 15 * 60_000;

const keyFor = (userId: string) => `canvaschool:survey:${userId}`;

/**
 * True once today's survey prompt is due for this user. Keyed by user, not
 * browser, because classroom tablets are shared. Without storage (private
 * mode) it never asks, rather than asking on every visit.
 */
export const useSurveyPrompt = (
  userId: string | null,
  random: () => number = Math.random,
): [due: boolean, dismiss: () => void] => {
  const [due, setDue] = useState(false);

  useEffect(() => {
    if (!userId) {
      return;
    }
    const today = new Date().toDateString();
    try {
      if (window.localStorage.getItem(keyFor(userId)) === today) {
        return;
      }
    } catch {
      return;
    }
    const delay =
      SURVEY_MIN_DELAY_MILLISECONDS +
      random() *
        (SURVEY_MAX_DELAY_MILLISECONDS - SURVEY_MIN_DELAY_MILLISECONDS);
    const timer = window.setTimeout(() => {
      try {
        // Another tab of the same student may have asked in the meantime.
        if (window.localStorage.getItem(keyFor(userId)) === today) {
          return;
        }
        window.localStorage.setItem(keyFor(userId), today);
      } catch {
        return;
      }
      setDue(true);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [userId, random]);

  return [due, useCallback(() => setDue(false), [])];
};
